import React, { useCallback, useEffect, useMemo } from 'react';
import {
  LuBell,
  LuSun,
  LuMoon,
  LuLogOut,
  LuUser,
  LuMenu,
} from 'react-icons/lu';
import Popover from '@/components/atoms/Popover';
import AppButton from '@/components/atoms/AppButton';
import { useAuth0 } from '@auth0/auth0-react';
import DateTimeRangePicker from '@/components/organism/DateTimeRangePicker';
import { useDateFilter } from '@/context/dateFilter.context';
import moment from 'moment-timezone';
import { getSelectedTimeZone } from '@/utils/dateTime';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/context/theme.context';
import { SETTINGS_KEY } from '@/utils/settings';
import { HEADER_PROPS } from '@/constant/layout/layout.constant';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';
import { LAYOUT_TEST_IDS as TID } from '@/constant/testIds/layout.testIds';
import Breadcrumbs from './BreadCrumb';

const Header = ({
  onMobileMenuToggle,
}: {
  onMobileMenuToggle?: () => void;
}) => {
  const { logout } = useAuth0();
  const user = JSON.parse(localStorage?.getItem('user') || '{}');

  const { isVisible, setFilterValues, filterValues, isDashboard } =
    useDateFilter();

  const selectedTimezone = getSelectedTimeZone();
  const location = useLocation();
  const { toggleTheme, isDark, theme, setTheme } = useTheme();

  const handleLogout = useCallback(() => {
    const settings = localStorage.getItem(SETTINGS_KEY);
    localStorage.clear();
    sessionStorage.clear();
    if (settings) localStorage.setItem(SETTINGS_KEY, settings);
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  useEffect(() => {
    if (isDashboard) {
      const now = moment().tz(selectedTimezone);
      setFilterValues({
        startDate: now.clone().subtract(6, 'day').startOf('day').format(),
        endDate: now.clone().endOf('day').format(),
      });
    } else {
      setFilterValues({ startDate: '', endDate: '' });
    }
  }, [location?.pathname]);

  const dashboardDefaultRange = useMemo(() => {
    if (isDashboard) {
      const now = moment().tz(selectedTimezone);
      return {
        startDate: now.clone().subtract(6, 'day').startOf('day').format(),
        endDate: now.clone().endOf('day').format(),
      };
    }
    return undefined;
  }, [location.pathname, selectedTimezone, isDashboard]);

  const handleFilterChange = (val: any) => {
    const fmt = (d: any) =>
      d ? moment.tz(d, selectedTimezone).format() : undefined;
    setFilterValues({
      startDate: fmt(val?.startDate),
      endDate: fmt(val?.endDate),
    });
  };

  return (
    <div
      data-testid={tid(TID.HEADER)}
      className="flex h-14 flex-col sticky top-0 z-[1000]"
    >
      <div
        className={`h-14 flex justify-between items-center px-4 ${APP_BG.base} border-b ${APP_BORDER.filterRow}`}
      >
        {/* LEFT */}
        <div className="flex items-center gap-2 md:gap-4">
          {/* Hamburger — mobile only */}
          <AppButton
            data-testid={tid(TID.HEADER_MOBILE_MENU_BTN)}
            className="md:hidden"
            variant="neutral"
            shape="square"
            size="lg"
            icon={<LuMenu />}
            onClick={onMobileMenuToggle}
            aria-label="Open menu"
          />
          <div className="hidden sm:block">
            <Breadcrumbs />
          </div>
        </div>

        {/* RIGHT */}
        <div className="flex items-center mr-2 justify-end gap-2">
          {/* ── DATE RANGE PICKER ── */}
          {isVisible && (
            <DateTimeRangePicker
              value={{
                startDate: filterValues?.startDate
                  ? String(filterValues.startDate)
                  : null,
                endDate: filterValues?.endDate
                  ? String(filterValues.endDate)
                  : null,
              }}
              onChange={handleFilterChange}
              onReset={() => setFilterValues({ startDate: '', endDate: '' })}
              timezone={selectedTimezone}
              showShortcuts
              defaultDateRange={isDashboard ? dashboardDefaultRange : undefined}
            />
          )}

          {/* THEME — hidden on mobile */}
          <div className="hidden sm:flex">
            <AppButton
              data-testid={tid(TID.HEADER_THEME_TOGGLE)}
              variant="ghost"
              shape="square"
              size="md"
              icon={
                isDark ? (
                  <LuSun className="w-4 h-4" />
                ) : (
                  <LuMoon className="w-4 h-4" />
                )
              }
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              aria-label={
                isDark ? HEADER_PROPS.THEME_LIGHT : HEADER_PROPS.THEME_DARK
              }
              title={
                isDark ? HEADER_PROPS.THEME_LIGHT : HEADER_PROPS.THEME_DARK
              }
            />
          </div>

          {/* NOTIFICATION */}
          <Popover
            testid={tid(TID.HEADER_NOTIFICATION_POPOVER)}
            title={HEADER_PROPS.NOTIFICATION_TITLE}
            button={<LuBell className={`text-[18px] ${APP_TEXT.soft}`} />}
          />

          {/* USER */}
          <Popover
            testid={tid(TID.HEADER_USER_POPOVER)}
            title={HEADER_PROPS.PROFILE_TITLE}
            popOverList={[
              {
                name: user?.name,
                url: user?.picture,
                link: 'profile',
                description: user?.nickname,
              },
            ]}
            button={
              user?.picture ? (
                <img
                  src={user.picture}
                  className="w-7 h-7 rounded-lg object-cover"
                />
              ) : (
                <LuUser className={`w-[18px] h-[18px] ${APP_TEXT.soft}`} />
              )
            }
            content={
              <div
                className={`${APP_BG.elevated} border ${APP_BORDER.card} rounded-xl overflow-hidden`}
              >
                <AppButton
                  data-testid={tid(TID.HEADER_LOGOUT_BTN)}
                  variant="danger"
                  size="sm"
                  icon={<LuLogOut className="w-4 h-4" />}
                  className="w-full justify-start rounded-none"
                  onClick={handleLogout}
                  aria-label={HEADER_PROPS.LOGOUT}
                >
                  {HEADER_PROPS.LOGOUT}
                </AppButton>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default Header;
